import { useCallback, useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, Dialog, Stack, Typography } from '@mui/material';

import { postSubscriptionAtom } from '@/interfaces/subscription';
import PaymentDialog from '@/components/common/PaymentDialog';

interface ManageSubscriptionDialogProps {
  sensorId: string;
  planName: string;
  onClose: (reason?: string) => void;
}

interface PlanCardProps {
  isCurrent: boolean;
  title: string;
  content: string[];
  rate: string[];
  buttonText: string;
  onClick: () => void;
}
function PlanCard({ isCurrent, title, content, rate, buttonText, onClick }: PlanCardProps) {
  return (
    <Stack gap={3} sx={{ width: 280, boxShadow: 2, p: 2 }}>
      <Typography variant="h5" fontWeight="bold" textAlign="center">
        {title}
      </Typography>
      <Stack>
        {content.map((text, i) => (
          <Typography key={'content' + i} fontWeight={i === 0 ? 'bold' : ''}>
            {`${i + 1}) ${text}`}
          </Typography>
        ))}
      </Stack>
      <Stack sx={{ mt: 'auto' }}>
        {rate.map((text, i) => (
          <Typography key={'rate' + i} fontSize={18} textAlign="center">
            {text}
          </Typography>
        ))}
      </Stack>
      <Button variant="contained" disabled={isCurrent} onClick={onClick}>
        {buttonText}
      </Button>
    </Stack>
  );
}

// TODO: Get plans from API
interface Plan {
  level: number;
  name: string;
  label: string;
  content: string[];
  rateText: string[];
  rate?: number;
}
const plans: Plan[] = [
  {
    level: 0,
    name: 'FREE',
    label: 'Free',
    content: [
      // NOTE Toss payment 심사를 위해 주석처리
      // '데이터 저장 기간 : 30일',
      '실시간 온도 / 위치 모니터링',
      '알람기능(App PUSH, E-mail)',
      '데이터 다운로드(PDF)',
      '인터벌 조절 가능',
      '1년 무상 A/S',
      '사용자 1인',
    ],
    rateText: ['Free'],
  },
  {
    level: 1,
    name: 'BASIC',
    label: 'Basic',
    content: [
      // NOTE Toss payment 심사를 위해 주석처리
      // '데이터 저장 기간 : 2년',
      '실시간 온도 / 위치 모니터링',
      '알람기능(SMS 10건/월, App PUSH, E-mail)',
      '데이터 다운로드(PDF)',
      '인터벌 조절 가능',
      '1년 무상 A/S',
    ],
    rateText: ['18,000원/년', '2,300원/월'],
    rate: 18000,
  },
  {
    level: 2,
    name: 'PREMIUM',
    label: 'Premium',
    content: [
      // NOTE Toss payment 심사를 위해 주석처리
      // '데이터 저장 기간 : 5년',
      '실시간 온도 / 위치 모니터링',
      '알람기능(SMS 30건/월, App PUSH, E-mail)',
      '데이터 다운로드(PDF)',
      '인터벌 조절 가능',
      '1년 무상 A/S',
      '21 CFR part 11',
      'API 지원',
    ],
    rateText: ['45,000원/년', '4,500원/월'],
    rate: 45000,
  },
];
const PLAN_LEVEL = ['FREE', 'BASIC', 'PREMIUM'];

export default function ManageSubscriptionDialog({ sensorId, planName, onClose }: ManageSubscriptionDialogProps) {
  const { mutate: postSubscription, data: postSubscriptionSuccess } = useAtomValue(postSubscriptionAtom);

  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState<Plan>();
  const { t } = useTranslation();
  const currentLevel = PLAN_LEVEL.indexOf(planName);

  useEffect(() => {
    if (postSubscriptionSuccess) {
      onClose('updated');
    } else if (postSubscriptionSuccess === false) {
      window.alert(t('Common.message.serverErrorOccurred'));
    }
  }, [onClose, postSubscriptionSuccess, t]);

  function getButtonText(level: number) {
    if (level < currentLevel) return t('Devices.label.downgrade');
    if (level > currentLevel) return t('Devices.label.upgrade');
    return t('Devices.label.currentSubscription');
  }

  function onPlanClick(newPlan: Plan) {
    if (newPlan.level === 0) {
      postSubscription({ sensorId, plan: newPlan.name });
    } else {
      setPlan(newPlan);
      setOpen(true);
    }
  }
  const onDialogClose = useCallback(
    (reason?: string) => {
      if (reason === 'escapeKeyDown') return;
      if (reason === 'updated') {
        onClose('updated');
      }
      setOpen(false);
    },
    [onClose],
  );

  return (
    <>
      <Stack gap={2} sx={{ p: 4 }}>
        <Stack direction="row" gap={2}>
          {plans.map((plan) => (
            <PlanCard
              key={plan.label}
              isCurrent={planName === plan.name}
              title={plan.label}
              content={plan.content}
              rate={plan.rateText}
              buttonText={getButtonText(plan.level)}
              onClick={() => onPlanClick(plan)}
            />
          ))}
        </Stack>
        <Link to="/refund-policy" target="_blank" style={{ textDecoration: 'none' }}>
          <Typography color="primary" textAlign="center" sx={{ textDecoration: 'underline' }}>
            {t('Devices.label.checkRefundPolicy')}
          </Typography>
        </Link>
        <Button
          variant="contained"
          onClick={() => onClose()}
          sx={{ alignSelf: 'center', bgcolor: '#CCC', color: 'black' }}
        >
          {t('Common.label.cancel')}
        </Button>
      </Stack>

      <Dialog
        open={open}
        onClose={(_, reason) => onDialogClose(reason)}
        sx={{ '& .MuiPaper-root': { maxWidth: 'unset' } }}
      >
        <PaymentDialog sensorId={sensorId} orderName={plan?.name} amount={plan?.rate} onClose={onDialogClose} />
      </Dialog>
    </>
  );
}
